/**
 * What is about to run out: certificates, document reviews, and work that is
 * due — found by looking, rather than waiting to be asked.
 *
 * Three things shape this file.
 *
 * **It reminds three times and then stops escalating.** Thirty days, fifteen,
 * seven, and then once more when the date passes. Acknowledging a notice does
 * not silence the thing; it silences *that* warning, and the next band brings it
 * back. A certificate that expires anyway after somebody clicked "later" three
 * times is a different failure from one nobody was told about, and the log shows
 * which happened.
 *
 * **Nothing is reported that the operator cannot see.** Every source is behind
 * a permission the platform already enforces, and the scan checks it before
 * reading. An assistant that says "the aerodrome certificate expires in nine
 * days" to somebody without `document:read` has leaked the existence, the date
 * and the state of a record they were not given — quietly, and in a place nobody
 * audits.
 *
 * **Only what is in force.** A document that is not issued is not carrying
 * anything, so its dates are not a deadline — the same rule the library's own
 * `attention` uses, borrowed rather than re-derived so the two never disagree.
 */
import type { DataSource, Principal } from "../api/dataSource";
import { PERMISSION, hasPermission } from "../api/dataSource";
import { isOpen } from "../domain/workOrders";
import { isCertificate } from "../domain/certificates";

/** How near it is, in the bands an operator is warned at. 0 means the date has passed. */
export type DueBand = 30 | 15 | 7 | 0;

export const BANDS: readonly DueBand[] = [30, 15, 7, 0];

export interface DueItem {
  /** Stable across scans, so an acknowledgement sticks to the thing rather than the reading. */
  id: string;
  kind: "certificate" | "document" | "review" | "service";
  label: string;
  /** The record's own words for why it is here. */
  detail: string;
  /** Days from now; negative once the date has passed. */
  days: number;
  band: DueBand;
  href?: string;
}

/** Which warning a number of days falls into, or null when it is not near yet. */
export function bandFor(days: number): DueBand | null {
  if (days < 0) return 0;
  if (days <= 7) return 7;
  if (days <= 15) return 15;
  if (days <= 30) return 30;
  return null;
}

const dayCount = (iso: string | undefined, now: number): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.round((t - now) / 86_400_000);
};

/** "in 14 days", "today", "23 days ago" — the phrase an operator would use. */
export function whenWords(days: number): string {
  if (days === 0) return "today";
  if (days > 0) return `in ${days} day${days === 1 ? "" : "s"}`;
  const n = Math.abs(days);
  return `${n} day${n === 1 ? "" : "s"} ago`;
}

export async function scanDue(
  source: DataSource,
  siteId: string,
  principal: Principal,
  now: number = Date.now(),
): Promise<DueItem[]> {
  const items: DueItem[] = [];

  if (hasPermission(principal, PERMISSION.documentsRead)) {
    const docs = await source.listDocuments({ siteId }).catch(() => []);
    for (const d of docs) {
      /* Not issued means not in force, so its dates are not deadlines. */
      if (d.status !== "issued") continue;

      const expiry = dayCount(d.expiresOn, now);
      if (expiry !== null) {
        const band = bandFor(expiry);
        if (band !== null) {
          const certificate = isCertificate(d);
          items.push({
            id: `doc-expiry-${d.id}`,
            kind: certificate ? "certificate" : "document",
            label: d.reference ? `${d.reference} · ${d.title}` : d.title,
            detail: `${certificate ? "Certificate" : d.type} ${expiry < 0 ? "expired" : "expires"} ${whenWords(expiry)}${d.revision ? ` · rev ${d.revision}` : ""}`,
            days: expiry,
            band,
            href: `/library/${d.id}`,
          });
        }
      }

      const review = dayCount(d.reviewDueOn, now);
      if (review !== null) {
        const band = bandFor(review);
        if (band !== null) {
          items.push({
            id: `doc-review-${d.id}`,
            kind: "review",
            label: d.reference ? `${d.reference} · ${d.title}` : d.title,
            detail: `Review ${review < 0 ? "was due" : "due"} ${whenWords(review)}`,
            days: review,
            band,
            href: `/library/${d.id}`,
          });
        }
      }
    }
  }

  if (hasPermission(principal, PERMISSION.workOrdersRead)) {
    const page = await source.listWorkOrders({ siteId }).catch(() => ({ items: [], truncated: false }));
    for (const w of page.items) {
      /* A closed order's due date is history, not a deadline. */
      if (!isOpen(w.status)) continue;
      const due = dayCount(w.dueAt, now);
      if (due === null) continue;
      const band = bandFor(due);
      if (band === null) continue;
      items.push({
        id: `wo-due-${w.id}`,
        kind: "service",
        label: `${w.number} · ${w.title}`,
        detail: `P${w.priority} · ${w.status} · ${due < 0 ? "was due" : "due"} ${whenWords(due)}`,
        days: due,
        band,
        href: `/work-orders/${w.id}`,
      });
    }
  }

  /* Worst first: overdue, then nearest. */
  return items.sort((a, b) => a.days - b.days);
}

/* ── what the operator has already been told ──────────────────────────── */

export interface DueState {
  /** id → the band it was acknowledged at. The next band down warns again. */
  acked: Record<string, DueBand>;
  /** Cancelled outright: never warn about this again. */
  stopped: string[];
}

const EMPTY: DueState = { acked: {}, stopped: [] };
const PREFIX = "skylogics.assistant.due";

export function loadDueState(who: string): DueState {
  try {
    const raw = localStorage.getItem(`${PREFIX}.${who}`);
    if (!raw) return { acked: {}, stopped: [] };
    const v = JSON.parse(raw) as Partial<DueState>;
    return { acked: v.acked ?? {}, stopped: Array.isArray(v.stopped) ? v.stopped : [] };
  } catch {
    return { ...EMPTY, acked: {}, stopped: [] };
  }
}

export function saveDueState(who: string, state: DueState): void {
  try {
    localStorage.setItem(`${PREFIX}.${who}`, JSON.stringify(state));
  } catch {
    /* the session still works; it simply forgets what was acknowledged */
  }
}

/**
 * Which of these the operator has not yet been warned about at this band.
 *
 * The comparison is the whole mechanism: an acknowledgement records the band it
 * was made at, and a lower band — one that is nearer, or past — is a new
 * warning. So "later" at thirty days is answered again at fifteen, and again at
 * seven, and again when it lapses, while "stop" ends it for good.
 */
export function unseen(items: readonly DueItem[], state: DueState): DueItem[] {
  return items.filter((i) => {
    if (state.stopped.includes(i.id)) return false;
    const acked = state.acked[i.id];
    return acked === undefined || i.band < acked;
  });
}
